import { isAxiosError } from "axios"
import { AutoForm } from "components/AutoForm/AutoForm"
import Button from "components/button"
import { CreditCardForm } from "components/CreditCardForm/CreditCardForm"
import {
  HousingForm,
  HousingFormProps,
} from "components/HousingForm/HousingForm"
import Input from "components/input"
import { Item } from "components/Item/Item"
import { ItemContainer } from "components/ItemContainer/ItemContainer"
import { LandForm } from "components/LandForm/LandForm"
import Loading from "components/loading"
import { PersonalForm } from "components/PersonalForm/PersonalForm"
import ResultBox from "components/result-box"
import global from "global"
import { Dispatch, SetStateAction, useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import { toast } from "react-toastify"
import request from "request"
import { useDispatch, useSelector } from "store/hooks"
import {
  branch,
  educations,
  employmentLevels,
  employmentSpheres,
  familyStatus,
  genders,
  productType,
  states,
  yesNoCoBorrower,
} from "utils/constants"
import { getRiskLabel } from "utils/getRiskLabel"
import { normalizeText } from "utils/normalizeText"
import prettierNumber from "utils/prettier-number"
import styles from "./scoring.module.scss"

export interface ScoringForm {
  branch: string
  gender: string
  familyStatus: string
  employmentSphere: string
  education: string
  collateral: string
  coBorrower: string
  productType: string
  employmentLevel: string
  loanDuration: string
  age: string
  monthlyIncome: string
  workExperience: string
  dependents: string
  cbj_black_list: string
  internal_black_list: string
  inspector_legal_cases: string
  total_personal_expenses_monthly: string
  active_ahli_bank_monthly_payment: string
  selected_monthly_payments: string[]
}

const Scoring = () => {
  const { id } = useParams()
  const user = useSelector((state) => state.user)
  const contextApplication = useSelector((state) => state.application)
  const dispatch = useDispatch()

  const [macroData, setMacroData] = useState<global.microData>()
  const [pending, setPending] = useState(false)
  const [formValid, setFormValid] = useState(false)

  const initialFormState: ScoringForm = {
    branch: "",
    gender: "",
    familyStatus: "",
    employmentSphere: "",
    education: "",
    collateral: "",
    coBorrower: "",
    productType: "",
    employmentLevel: "",
    loanDuration: "",
    age: "",
    monthlyIncome: "",
    workExperience: "",
    dependents: "",
    cbj_black_list: "",
    internal_black_list: "",
    inspector_legal_cases: "",
    total_personal_expenses_monthly: "",
    active_ahli_bank_monthly_payment: "",
    selected_monthly_payments: [],
  }

  const [form, setForm] = useState<ScoringForm>(initialFormState)

  const verification = [
    {
      title: "CBJ Black list / Returned Cheques",
      id: "cbj_black_list",
      key: "cbj_black_list",
    },
    {
      title: "Internal Black list",
      id: "internal_black_list",
      key: "internal_black_list",
    },
    {
      title: "Inspector / Financial legal cases",
      id: "inspector_financial_legal_cases",
      key: "inspector_legal_cases",
    },
  ]

  const numberInputs = [
    {
      title: "Loan duration",
      key: "loanDuration",
      type: "integer",
      min: 1,
      max: 300,
    },
    { title: "Age", key: "age", type: "integer", min: 18, max: 80 },
    {
      title: "Monthly income",
      key: "monthlyIncome",
      type: "float",
      min: 0,
      max: 30000,
    },
    {
      title: "Work experience",
      key: "workExperience",
      type: "float",
      min: 0,
      max: 50,
    },
    {
      title: "Dependants",
      key: "dependents",
      type: "integer",
      min: 0,
      max: 40,
    },
    {
      title: "Total personal expenses (monthly)",
      key: "total_personal_expenses_monthly",
      type: "integer",
      min: 0,
    },
    {
      title: "Active Ahli Bank monthly payment",
      key: "active_ahli_bank_monthly_payment",
      type: "integer",
      min: 0,
      max: 1000000,
    },
  ]

  const selectInput = [
    { title: "Branch", id: "branch", options: branch, key: "branch" },
    { title: "Gender", id: "gender", options: genders, key: "gender" },
    {
      title: "Family status",
      id: "familyStatus",
      options: familyStatus,
      key: "familyStatus",
    },
    {
      title: "Employment sphere",
      id: "employmentSphere",
      options: employmentSpheres,
      key: "employmentSphere",
    },
    {
      title: "Education",
      id: "education",
      options: educations,
      key: "education",
    },
    {
      title: "Collateral",
      id: "collateral",
      options: yesNoCoBorrower,
      key: "collateral",
    },
    {
      title: "Co borrower",
      id: "coBorrower",
      options: yesNoCoBorrower,
      key: "coBorrower",
    },
    {
      title: "Type of product",
      id: "type_of_product",
      options: productType,
      key: "productType",
    },
    {
      title: "Employment level",
      id: "employmentLevel",
      options: employmentLevels,
      key: "employmentLevel",
    },
  ]
  const multipleInput = [
    {
      title: "Selected monthly payments",
      id: "selected_monthly_payments",
      options: contextApplication?.data?.ocr_log?.installments
        ? Object.entries(contextApplication.data.ocr_log.installments).map(
            ([key, value]) => {
              let labelKey = key
                .replace(/_/g, " ")
                .replace(
                  /\b(loan|credit card)\b/i,
                  (match) => match.charAt(0).toUpperCase() + match.slice(1),
                )

              const match = key.match(/\d+$/)
              if (match) {
                labelKey = labelKey.replace(/\d+$/, `#${match[0]}`)
              }

              return {
                key,
                value,
                label: `${labelKey} — ${value}`,
              }
            },
          )
        : [],
      key: "selected_monthly_payments",
    },
  ]
  const showInfo = [
    {
      key: "Branch",
      value: branch.find(
        (e) => e.key === contextApplication.data?.scoring_log?.branch,
      )?.value,
    },
    {
      key: "Gender",
      value: genders.find(
        (e) => e.key === contextApplication.data?.scoring_log?.gender,
      )?.value,
    },
    {
      key: "Family status",
      value: familyStatus.find(
        (e) => e.key === contextApplication.data?.scoring_log?.familyStatus,
      )?.value,
    },
    {
      key: "Employment sphere",
      value: employmentSpheres.find(
        (e) => e.key === contextApplication.data?.scoring_log?.employmentSphere,
      )?.value,
    },
    {
      key: "Collateral",
      value: yesNoCoBorrower.find(
        (e) => e.key === contextApplication.data?.scoring_log?.collateral,
      )?.value,
    },
    {
      key: "Co borrower",
      value: yesNoCoBorrower.find(
        (e) => e.key === contextApplication.data?.scoring_log?.coBorrower,
      )?.value,
    },
    {
      key: "Type of product",
      value: productType.find(
        (e) => e.key === contextApplication.data?.scoring_log?.productType,
      )?.value,
    },
    {
      key: "Employment level",
      value: employmentLevels.find(
        (e) => e.key === contextApplication.data?.scoring_log?.employmentLevel,
      )?.value,
    },
    {
      key: "Education",
      value: educations.find(
        (e) => e.key === contextApplication.data?.scoring_log?.education,
      )?.value,
    },
    {
      key: "Loan duration",
      value: contextApplication.data?.scoring_log?.loanDuration,
    },
    { key: "Age", value: contextApplication.data?.scoring_log?.age },
    {
      key: "Monthly income",
      value: contextApplication.data?.scoring_log?.monthlyIncome + " JOD",
    },
    {
      key: "Work experience",
      value: contextApplication.data?.scoring_log?.workExperience,
    },
    {
      key: "Dependents",
      value: contextApplication.data?.scoring_log?.dependents,
    },
    {
      key: "Total personal expenses (monthly)",
      value:
        contextApplication.data?.scoring_log?.total_personal_expenses_monthly,
    },
    {
      key: "Selected monthly payments",
      value:
        Object.keys(
          contextApplication.data?.scoring_log?.selected_monthly_payments ?? {},
        ).length > 0
          ? Object.entries(
              contextApplication.data?.scoring_log?.selected_monthly_payments ??
                {},
            )
              .map(([key, value]) => {
                let labelKey = key
                  .replace(/_/g, " ")
                  .replace(
                    /\b(loan|credit card)\b/i,
                    (match) => match.charAt(0).toUpperCase() + match.slice(1),
                  )

                const match = key.match(/\d+$/)
                if (match) {
                  labelKey = labelKey.replace(/\d+$/, `#${match[0]}`)
                }

                return `${labelKey} — ${value}`
              })
              .join("\n")
          : "-",
    },

    {
      key: "Over Indebtedness Ratio",
      value:
        Math.round(
          Number(
            contextApplication.data?.scoring_log?.over_independence_ratio,
          ) * 100,
        ) + "%",
    },
    {
      key: "Active Ahli Bank monthly payment",
      value: Number(
        contextApplication.data?.scoring_log?.active_ahli_bank_monthly_payment,
      ),
    },
    {
      key: "Requested loan’s monthly payment",
      value: Number(
        contextApplication.data?.scoring_log?.requested_loan_monthly_payment,
      ).toFixed(2),
    },
    {
      key: "Sum of selected monthly payment",
      value: Number(
        contextApplication.data?.scoring_log?.sum_of_selected_payments,
      ),
    },
  ]

  const showLoanInfo = [
    {
      key: "Customer ID",
      value: contextApplication.data?.customerId,
    },
    {
      key: "Application ID",
      value: contextApplication.data?.applicationId,
    },
    {
      key: "Amount",
      value: prettierNumber(contextApplication.data?.loanAmount ?? 0) + " JOD",
    },
    {
      key: "Interest rate",
      value: contextApplication.data?.interestRate + "%",
    },
    { key: "Product", value: contextApplication.data?.product },
  ]

  useEffect(() => {
    if (user.role === "expert") return
    request
      .get("/admin/constant_values")
      .then((res) => setMacroData(res.data))
      .catch((error) => {
        const errorMessage =
          error?.response?.data?.message ||
          error.message ||
          "An unknown error occurred."
        toast.error(`Error fetching constant values: ${errorMessage}`)
      })
  }, [user.role])

  const handleCalculate = () => {
    setPending(true)
    const installmentsMap: Record<string, any> =
      (contextApplication.data as any)?.ocr_log?.installments || {}


    const requestData = {
      ...form,
      loanDuration: Number(form.loanDuration),
      interestRate: Number(contextApplication.data?.interestRate),
      age: Number(form.age),
      monthlyIncome: Number(form.monthlyIncome),
      workExperience: Number(form.workExperience),
      dependents: Number(form.dependents),
      grantedLimitAM: Number(contextApplication.data?.loanAmount),
      total_personal_expenses_monthly: Number(
        form.total_personal_expenses_monthly,
      ),
      selected_monthly_payments: form.selected_monthly_payments,
      length_of_service:
        "length_of_service" in form
          ? Number(form.length_of_service)
          : undefined,
    }

    request
      .post(`/applications/${id}/scoring`, requestData)
      .then(() => {
        setTimeout(() => dispatch.application.FETCH_APPLICATION_BY_ID(id), 1000)
      })
      .catch((e: unknown) => {
        if (isAxiosError(e)) {
          const errorMessage =
            e.response?.status === 409
              ? e.response.data.detail
              : e?.response?.data?.detail[0]?.msg
          if (errorMessage) toast.error(errorMessage)
        }
      })
      .finally(() => setPending(false))
  }

  if (user.role !== "expert" && !macroData) return null

  return (
    <div className={styles.home}>
      <div className={styles.fields}>
        <div className={styles.creditDataBox}>
          <div className={styles.title}>Credit data</div>
          <div className={styles.list}>
            {showLoanInfo.map(({ key, value }) => (
              <div className={styles.item} key={key}>
                <div className={styles.key}>{key}</div>
                <div className={styles.value}>{value}</div>
              </div>
            ))}
            {(user.role !== "expert" ||
              contextApplication.data?.scoring_log?.threshold) && (
              <>
                <div className={styles.item}>
                  <div className={styles.key}>Scoring Threshold</div>
                  <div className={styles.value}>
                    {contextApplication.data?.scoring_log?.threshold
                      ? Number(contextApplication.data?.scoring_log?.threshold)
                      : Number(macroData?.threshold) * 100}
                    %
                  </div>
                </div>
                <div className={styles.item}>
                  <div className={styles.key}>
                    Over Indebtedness Ratio Threshold
                  </div>
                  <div className={styles.value}>
                    {Number(macroData?.hard_oir_threshold) * 100}%
                  </div>
                </div>
                <div className={styles.item}>
                  <div className={styles.key}>DBR Threshold</div>
                  <div className={styles.value}>
                    {(Number(macroData?.dbr_threshold) * 100)?.toFixed(2)}%
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        {contextApplication.data?.scoring_log && (
          <>
            <div className={styles.creditDataBox}>
              <div className={styles.title}>Customer data</div>
              <div className={styles.list}>
                {showInfo.map(({ key, value }) => (
                  <div className={styles.item} key={key}>
                    <div className={styles.key}>{key}</div>
                    <div className={styles.value}>{value}</div>
                  </div>
                ))}
                {contextApplication?.data?.scoring_log?.is_approved !==
                  null && (
                  <div className={styles.item}>
                    <div className={styles.key}>Scoring prediction</div>
                    <div className={styles.value}>
                      {contextApplication.data?.scoring_log?.prediction}%
                    </div>
                  </div>
                )}
              </div>
            </div>
            <ItemContainer title={"Product data"}>
              <>
                {contextApplication?.data?.product === "Housing" && (
                  <Item
                    title="Loan type"
                    value={contextApplication?.data?.scoring_log?.loan_type}
                  />
                )}
                {(contextApplication?.data?.product === "Housing" ||
                  contextApplication?.data?.product === "Land purchase" ||
                  contextApplication?.data?.product === "Mortgage" ||
                  contextApplication?.data?.product === "Auto") && (
                  <Item
                    title="Property value"
                    value={
                      contextApplication?.data?.scoring_log?.property_value
                    }
                  />
                )}
                {(contextApplication?.data?.product === "Housing" ||
                  contextApplication?.data?.product === "Auto") && (
                  <Item
                    title="Property age at loan maturity"
                    value={
                      contextApplication?.data?.scoring_log?.property_age_at_end
                    }
                  />
                )}
                {(contextApplication?.data?.product === "Housing" ||
                  contextApplication?.data?.product === "Land purchase" ||
                  contextApplication?.data?.product === "Mortgage") && (
                  <Item
                    title="Property outside boundaries of regulation"
                    value={
                      contextApplication?.data?.scoring_log
                        ?.property_outside_regulations
                    }
                  />
                )}
                {contextApplication?.data?.product === "Auto" && (
                  <Item
                    title="Car evaluation score"
                    value={
                      contextApplication?.data?.scoring_log
                        ?.car_evaluation_score
                    }
                  />
                )}
                {contextApplication?.data?.product === "Credit Card" && (
                  <Item
                    title="Credit card limit"
                    value={
                      contextApplication?.data?.scoring_log?.credit_card_limit
                    }
                  />
                )}
                {contextApplication?.data?.product === "Personal" && (
                  <>
                    <Item
                      title="Target market list"
                      value={
                        contextApplication?.data?.scoring_log
                          ?.target_market_list
                      }
                    />
                    <Item
                      title="Length of service (in months)"
                      value={
                        contextApplication?.data?.scoring_log?.length_of_service
                      }
                    />
                    <Item
                      title="Against collateral"
                      value={contextApplication?.data?.scoring_log?.collateral}
                    />
                  </>
                )}
                {(contextApplication?.data?.product === "Housing" ||
                  contextApplication?.data?.product === "Land purchase" ||
                  contextApplication?.data?.product === "Mortgage" ||
                  contextApplication?.data?.product === "Auto") && (
                  <Item
                    title="LTV"
                    value={`${(contextApplication?.data?.scoring_log?.ltv * 100).toFixed(2)}%`}
                  />
                )}
                <Item
                  title="DBR"
                  value={`${(contextApplication?.data?.scoring_log?.dbr * 100).toFixed(2)}%`}
                />
              </>
            </ItemContainer>
            <ItemContainer title="Verification">
              <>
                <Item
                  title="CBJ Black list / Returned Cheques"
                  value={contextApplication?.data?.scoring_log.cbj_black_list}
                />
                <Item
                  title="Internal black list"
                  value={
                    contextApplication?.data?.scoring_log?.internal_black_list
                  }
                />
                <Item
                  title="Inspector / Financial legal cases"
                  value={
                    contextApplication?.data?.scoring_log?.inspector_legal_cases
                  }
                />
              </>
            </ItemContainer>
            <ItemContainer title="Risks">
              <>
                {contextApplication?.data?.risks !== null &&
                  Object.entries(contextApplication?.data?.risks).map(
                    ([key, value]) => (
                      <Item
                        key={key}
                        title={normalizeText(key)}
                        value={getRiskLabel(String(value))}
                      />
                    ),
                  )}
              </>
            </ItemContainer>
          </>
        )}
        {!contextApplication.data?.scoring_log && (
          <>
            <div className={styles.fieldGroup}>
              <div className={styles.fieldTitle}>Customer data</div>
              <div className={styles.fieldList}>
                {selectInput.map(({ title, id, options, key }) => (
                  <Input
                    key={id}
                    hasFilterText
                    title={title}
                    id={id}
                    value={form[key as keyof ScoringForm]}
                    type="select"
                    options={options}
                    onSelect={(option) =>
                      setForm({
                        ...form,
                        [key as keyof ScoringForm]: option.key,
                      })
                    }
                  />
                ))}

                {multipleInput.map(({ title, id, options, key }) => (
                  <Input
                    key={id}
                    hasFilterText
                    title={title}
                    id={id}
                    multiple
                    value={
                      Array.isArray(form[key as keyof ScoringForm])
                        ? form[key as keyof ScoringForm]
                        : []
                    }
                    type="select"
                    options={options.map(({ key, label }) => ({
                      key,
                      value: label,
                    }))}
                    onMultiSelect={(selectedKeys) => {
                      setForm({
                        ...form,
                        [key]: selectedKeys,
                      })
                    }}
                    placeholder="Selected monthly payments of financial institutions from CRIF"
                    disabled={!contextApplication.data?.ocr_log}
                  />
                ))}
                {numberInputs.map(({ title, key, type, min, max }) => (
                  <Input
                    key={key}
                    title={title}
                    value={form[key as keyof ScoringForm] || ""}
                    type={type as any}
                    onInput={(value) =>
                      setForm({ ...form, [key as keyof ScoringForm]: value })
                    }
                    min={min}
                    max={max}
                    placeholder={
                      key === "total_personal_expenses_monthly"
                        ? `min: ${min}`
                        : `min: ${min} max: ${max}`
                    }
                  />
                ))}
              </div>

              <div className={styles.fieldTitle}>Product data</div>
              {contextApplication.data?.product === "Housing" && (
                <HousingForm
                  form={form as HousingFormProps}
                  setForm={
                    setForm as Dispatch<SetStateAction<HousingFormProps>>
                  }
                  setFormValid={setFormValid}
                />
              )}
              {(contextApplication.data?.product === "Land purchase" ||
                contextApplication.data?.product === "Mortgage") && (
                <LandForm
                  form={form as LandForm}
                  setForm={setForm as Dispatch<SetStateAction<LandForm>>}
                  setFormValid={setFormValid}
                />
              )}
              {contextApplication?.data?.product === "Auto" && (
                <AutoForm
                  form={form as AutoForm}
                  setForm={setForm as Dispatch<SetStateAction<AutoForm>>}
                  setFormValid={setFormValid}
                />
              )}
              {contextApplication?.data?.product === "Credit Card" && (
                <CreditCardForm
                  form={form as CreditCardForm}
                  setForm={setForm as Dispatch<SetStateAction<CreditCardForm>>}
                  setFormValid={setFormValid}
                />
              )}
              {contextApplication?.data?.product === "Personal" && (
                <PersonalForm
                  form={form as PersonalForm}
                  setForm={setForm as Dispatch<SetStateAction<PersonalForm>>}
                  setFormValid={setFormValid}
                />
              )}
              <div className={styles.fieldTitle}>Verification</div>
              <div className={styles.fieldList}>
                {verification.map(({ title, id, key }) => (
                  <Input
                    key={id}
                    title={title}
                    id={id}
                    value={form[key as keyof ScoringForm]}
                    type="select"
                    options={states}
                    onSelect={(option) =>
                      setForm({
                        ...form,
                        [key]: option.key as keyof ScoringForm,
                      })
                    }
                  />
                ))}
              </div>
            </div>
            <div className={styles.footerBox}>
              <div className={styles.buttonGroup}>
                {formValid && (
                  <Button pending={pending} onClick={handleCalculate}>
                    Calculate
                  </Button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      <div className={styles.result}>
        {pending && (
          <div className={styles.resultBox}>
            <Loading size={50} />
          </div>
        )}
        {!pending && (
          <div className={styles.result}>
            <div className={styles.resultBox}>
              <ResultBox />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Scoring
